import React from 'react';
import { Button } from '../ui/Button';

interface BubbleAction {
  label: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  onClick: () => void;
}

interface Props {
  message: string;
  actions?: BubbleAction[];
  hint?: string;
  variant?: 'corner' | 'center';
  position?: 'top' | 'bottom';
}

export function TutorialMascot({
  message,
  actions,
  hint,
  variant = 'corner',
  position = 'bottom',
}: Props) {
  const wrapperClass =
    variant === 'center'
      ? 'fixed inset-0 flex flex-col items-center justify-center px-5 pointer-events-none'
      : position === 'top'
        ? 'fixed top-0 left-0 right-0 px-3 pt-[max(0.5rem,env(safe-area-inset-top))] flex flex-col items-center md:items-end md:right-6 md:left-auto md:top-6 md:max-w-[420px] pointer-events-none'
        : 'fixed bottom-0 left-0 right-0 px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] flex flex-col items-center md:items-end md:right-6 md:left-auto md:bottom-6 md:max-w-[420px] pointer-events-none';

  return (
    <div
      className={wrapperClass}
      style={{ zIndex: 64 }}
      role="dialog"
      aria-modal="true"
      aria-label="Tutorial"
    >
      <div
        className="relative bg-white rounded-2xl shadow-lg border border-ink-300/70 px-4 py-3 md:px-5 md:py-4 max-w-[440px] w-full pointer-events-auto animate-fade-up overflow-y-auto"
        style={{ marginBottom: variant === 'center' ? 16 : 8, maxHeight: '35vh' }}
      >
        <p className="font-body text-small md:text-body text-ink-900 leading-snug">{message}</p>
        {hint && (
          <p className="mt-2 font-display font-semibold text-small text-green-700 inline-flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-700 animate-pulse" />
            {hint}
          </p>
        )}
        {actions && actions.length > 0 && (
          <div className="mt-3 md:mt-4 flex flex-wrap gap-2 justify-end">
            {actions.map((a) => (
              <Button
                key={a.label}
                variant={a.variant ?? 'primary'}
                size="sm"
                onClick={a.onClick}
              >
                {a.label}
              </Button>
            ))}
          </div>
        )}
        <span
          aria-hidden
          className="absolute -bottom-2 left-1/2 -translate-x-1/2 md:left-auto md:right-10 md:translate-x-0 w-4 h-4 bg-white border-r border-b border-ink-300/70 rotate-45"
        />
      </div>

      <img
        src="/mascote.png"
        alt=""
        aria-hidden
        className={[
          variant === 'center'
            ? 'w-40 h-40 md:w-64 md:h-64'
            : 'w-28 h-28 md:w-56 md:h-56',
          'drop-shadow-xl pointer-events-none select-none',
          variant === 'corner' ? 'animate-float-2' : '',
        ].join(' ')}
      />
    </div>
  );
}
